import React, { useState } from 'react';
import { Shield, Check, HelpCircle, ChevronDown, ChevronUp, Target, Clock, Award, FileCheck, Users, Zap, Search } from 'lucide-react';
import UniversalPopupForm from '../../components/UniversalPopupForm';
import SEO from '../../components/SEO';

const ComprehensiveSearch = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const benefits = [
    {
      title: 'Avoid Refusals',
      description: 'Identify potential conflicts before filing, significantly reducing the risk of CIPO rejection.'
    },
    {
      title: 'Cost Savings',
      description: 'Prevent wasted government fees and legal expenses from filing unsuccessful applications.'
    },
    {
      title: 'Strategic Planning',
      description: 'Make informed decisions about trademark variations and alternative brand strategies.'
    },
    {
      title: 'Comprehensive Coverage',
      description: 'Search CIPO database, pending applications, and common law marks across Canada.'
    },
    {
      title: 'Expert Analysis',
      description: 'Receive professional interpretation of search results with actionable recommendations.'
    },
    {
      title: 'Peace of Mind',
      description: 'Proceed with confidence knowing your trademark has been thoroughly vetted.'
    }
  ];

  const faqs = [
    {
      question: 'What does a comprehensive trademark search include?',
      answer: 'Our comprehensive search includes the CIPO trademark database, pending applications, registered marks, common law marks, and confusingly similar variations. We provide a detailed report with expert analysis and recommendations.'
    },
    {
      question: 'How long does a trademark search take?',
      answer: 'Most comprehensive searches are completed within 3-5 business days. We provide a detailed written report with our findings and professional recommendations for your trademark application.'
    },
    {
      question: 'Is a trademark search required before filing?',
      answer: 'While not legally required, a comprehensive search is highly recommended. It helps identify potential conflicts early, saving you time and money by avoiding refusals and legal disputes.'
    },
    {
      question: 'What if my search reveals conflicts?',
      answer: 'If we find potential conflicts, our report will detail the issues and provide recommendations. This might include trademark modifications, alternative strategies, or proceeding with an informed understanding of the risks.'
    },
    {
      question: 'Can I file a trademark without a search?',
      answer: 'Yes, but it\'s risky. Without a search, you may face CIPO refusals, wasted fees, or legal challenges from existing trademark owners. A search investment protects your larger registration investment.'
    },
    {
      question: 'Does the search guarantee approval?',
      answer: 'While a clear search significantly improves approval chances, it cannot guarantee CIPO approval. However, it identifies the majority of potential obstacles and allows for informed decision-making.'
    }
  ];

  const searchPricing = [
    {
      name: 'Basic',
      features: [
        'CIPO Database Search',
        'Identical Marks Review',
        'Basic Conflict Analysis',
        'Search Summary Report',
        'Email Delivery'
      ],
      recommended: false,
      badge: null
    },
    {
      name: 'Standard',
      features: [
        'Everything in Basic',
        'Similar Marks Analysis',
        'Phonetic Similarity Search',
        'Detailed Written Report',
        'Risk Assessment',
        'Expert Recommendations',
        'Phone Consultation (30 min)'
      ],
      recommended: true,
      badge: 'Recommended'
    },
    {
      name: 'Deluxe',
      features: [
        'Everything in Standard',
        'Common Law Marks Search',
        'Domain Name Analysis',
        'Business Name Registry Check',
        'International Database Review',
        'Strategic Brand Protection Plan',
        'Extended Consultation (1 hour)',
        'Follow-up Search Updates (3 months)'
      ],
      recommended: false,
      badge: 'Best Seller'
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Comprehensive Trademark Search"
        description="Professional trademark search services across CIPO database and common law marks. Identify conflicts before filing to avoid costly rejections and legal issues."
        keywords="trademark search Canada, CIPO database search, brand name availability, trademark clearance search, pre-filing trademark search"
      />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-brand-600 to-brand-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85"
            alt="Trademark search and analysis"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block bg-white bg-opacity-20 rounded-full px-4 py-2 mb-6">
              <span className="text-white font-semibold">CIPO Trademark Search</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6">
              Comprehensive Trademark Search
            </h1>
            <p className="text-xl text-brand-100 leading-relaxed mb-8">
              Thorough CIPO database search to ensure your trademark is unique and registrable. Protect your investment before you file.
            </p>
            <button
              onClick={() => setIsPopupOpen(true)}
              className="bg-white text-brand-600 font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-200 shadow-xl"
            >
              Start Your Search
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Professional Help */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-800 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40"
                alt="Professional trademark search analysis"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="bg-green-100 rounded-full p-2">
                    <Award className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="font-bold text-gray-900">Thorough Analysis</span>
                </div>
                <p className="text-sm text-gray-600">Expert CIPO search & report</p>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Professional Advantage
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Professional Help Matters
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                A basic trademark search only scratches the surface. Our experts conduct comprehensive searches that identify confusingly similar marks, potential conflicts, and provide strategic recommendations.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <Target className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Deep Search Methodology</h3>
                    <p className="text-gray-600">We search beyond exact matches to find phonetic similarities, visual similarities, and translation equivalents.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <FileCheck className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Professional Interpretation</h3>
                    <p className="text-gray-600">Expert analysis of results with clear recommendations for your specific situation.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Save Time & Money</h3>
                    <p className="text-gray-600">Identify issues before filing, avoiding costly refusals and legal challenges.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Benefits of Comprehensive Search
            </h2>
            <p className="text-lg text-gray-600">
              Why every Canadian trademark application needs professional search
            </p>
          </div>

          <div className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 pt-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-brand-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-brand-100 min-w-full md:min-w-0 snap-center"
              >
                <div className="bg-brand-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CIPO Search Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-green-100 text-green-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                CIPO Expertise
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Navigate CIPO Requirements With Confidence
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our trademark search goes beyond simple database queries. We analyze results through the lens of CIPO's examination standards to predict potential objections and conflicts.
              </p>
              <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-2xl p-8 mb-6">
                <h3 className="font-bold text-gray-900 mb-4 text-xl">What We Search:</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">CIPO registered trademarks database</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Pending trademark applications</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Common law and unregistered marks</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Phonetic and visual similarities</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Domain names and business registrations</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => { if (window.LiveChatWidget) { window.LiveChatWidget.call('maximize'); } else { alert('Chat is loading... Please try again in a moment.'); } }}
                className="bg-gradient-to-r from-brand-600 to-brand-800 text-white font-semibold px-8 py-4 rounded-lg hover:from-brand-700 hover:to-brand-900 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Order Your Search
              </button>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-400 to-brand-500 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71"
                alt="CIPO search database"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 rounded-full p-2">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">CIPO Search</div>
                    <div className="text-sm text-gray-600">Experts</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fast & Reliable Service */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f"
                alt="Fast trademark search processing"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-brand-600 to-brand-800 text-white rounded-xl shadow-xl p-6 max-w-xs">
                <Zap className="w-8 h-8 mb-2" />
                <div className="font-bold text-lg">3-5 Day Turnaround</div>
                <div className="text-sm text-brand-100">Fast comprehensive search</div>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Quick & Efficient
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Fast Processing, Quality Results
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We understand that time is critical in brand development. Our streamlined search process delivers comprehensive results quickly without compromising thoroughness or accuracy.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-800 rounded-full p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Quick Turnaround</h3>
                    <p className="text-gray-600">Complete comprehensive search and detailed report within 3-5 business days.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-800 rounded-full p-3 flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Expert Consultation</h3>
                    <p className="text-gray-600">Review session to discuss findings and answer all your questions.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-800 rounded-full p-3 flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Detailed Report</h3>
                    <p className="text-gray-600">Comprehensive written report with clear recommendations and risk assessment.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our 3-Step Search Process
            </h2>
            <p className="text-lg text-gray-600">
              Simple, transparent, and comprehensive CIPO search
            </p>
          </div>

          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 pt-8">
            <div className="relative bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border border-brand-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl min-w-full md:min-w-0 snap-center">
              <div className="absolute -top-6 left-8 bg-gradient-to-r from-brand-600 to-brand-800 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                1
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Initial Consultation
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  We gather details about your proposed trademark, including the mark itself, goods/services, and your business plans to tailor our search strategy.
                </p>
              </div>
            </div>

            <div className="relative bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border border-brand-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl min-w-full md:min-w-0 snap-center">
              <div className="absolute -top-6 left-8 bg-gradient-to-r from-brand-600 to-brand-800 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                2
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Comprehensive Search
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Our experts conduct an extensive search of CIPO databases, common law marks, and related sources to identify any potential conflicts or issues.
                </p>
              </div>
            </div>

            <div className="relative bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border border-brand-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl min-w-full md:min-w-0 snap-center">
              <div className="absolute -top-6 left-8 bg-gradient-to-r from-brand-600 to-brand-800 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                3
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Report & Recommendations
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Receive a detailed written report with our findings, risk assessment, and strategic recommendations for proceeding with your trademark application.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trademark Search Packages
            </h2>
            <p className="text-lg text-gray-600">
              Choose the search depth that matches your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {searchPricing.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 ${
                  plan.recommended ? 'ring-2 ring-brand-600 scale-105' : ''
                }`}
              >
                {plan.badge && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-brand-600 to-brand-800 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                    {plan.badge}
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                  <div className="mb-6">
                    <p className="text-gray-600 text-sm">Contact us for pricing</p>
                  </div>
                  <ul className="space-y-3 mb-8 max-h-64 overflow-y-auto">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => { if (window.LiveChatWidget) { window.LiveChatWidget.call('maximize'); } else { alert('Chat is loading... Please try again in a moment.'); } }}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      plan.recommended
                        ? 'bg-gradient-to-r from-brand-600 to-brand-800 text-white hover:from-brand-700 hover:to-brand-900'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    Get a Quote
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about trademark search services
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="relative lg:sticky lg:top-8">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d"
                alt="Trademark consultation"
                className="relative rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-6">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Still Have Questions?</h3>
                <p className="text-gray-600 text-sm mb-4">Our trademark search experts are here to help answer all your questions.</p>
                <button
                  onClick={() => { if (window.LiveChatWidget) { window.LiveChatWidget.call('maximize'); } else { alert('Chat is loading... Please try again in a moment.'); } }}
                  className="w-full bg-gradient-to-r from-brand-600 to-brand-800 text-white font-semibold py-2 px-4 rounded-lg hover:from-brand-700 hover:to-brand-900 transition-all text-sm"
                >
                  Contact Us
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-start space-x-4 flex-1">
                      <HelpCircle className="w-6 h-6 text-brand-600 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900 text-lg pr-4">{faq.question}</span>
                    </div>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <div className="pl-10 text-gray-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab"
            alt="Canadian business district"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-900/95 via-brand-800/90 to-brand-700/95"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-brand-900/50 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block animate-bounce mb-8">
            <div className="bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30">
              <span className="text-white font-bold text-sm">🍁 Trusted by 500+ Canadian Businesses</span>
            </div>
          </div>

          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Avoid Costly Mistakes<br />
            <span className="bg-gradient-to-r from-brand-300 to-brand-400 bg-clip-text text-transparent">
              Search Before You File
            </span>
          </h2>
          
          <p className="text-2xl text-brand-100 mb-12 max-w-3xl mx-auto leading-relaxed">
            Protect your investment with a comprehensive CIPO search. Make informed decisions before you file.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <button
              onClick={() => { if (window.LiveChatWidget) { window.LiveChatWidget.call('maximize'); } else { alert('Chat is loading... Please try again in a moment.'); } }}
              className="group relative bg-white text-brand-600 font-bold px-10 py-5 rounded-xl hover:bg-gray-100 transform hover:scale-110 transition-all duration-300 shadow-2xl"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <Shield className="w-6 h-6" />
                <span className="text-lg">Order Your Search</span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-brand-400 to-brand-500 rounded-xl opacity-0 group-hover:opacity-20 blur transition-opacity duration-300"></div>
            </button>
            <button
              onClick={() => { if (window.LiveChatWidget) { window.LiveChatWidget.call('maximize'); } else { alert('Chat is loading... Please try again in a moment.'); } }}
              className="group relative bg-gradient-to-r from-brand-600 to-brand-800 text-white font-bold px-10 py-5 rounded-xl hover:from-brand-700 hover:to-brand-900 transform hover:scale-110 transition-all duration-300 shadow-2xl"
            >
              <span className="relative z-10 text-lg">Chat Now</span>
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">100%</div>
              <div className="text-brand-100 text-sm">Thorough Search</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">3-5</div>
              <div className="text-brand-100 text-sm">Day Turnaround</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">500+</div>
              <div className="text-brand-100 text-sm">Searches Done</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">Expert</div>
              <div className="text-brand-100 text-sm">Analysis</div>
            </div>
          </div>

          <p className="text-brand-200 mt-12 text-sm">
            Protecting Canadian businesses coast to coast • CIPO Search Specialists
          </p>
        </div>

        <div className="absolute top-10 left-10 w-20 h-20 bg-brand-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-brand-400/20 rounded-full blur-3xl animate-pulse"></div>
      </section>

      <UniversalPopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default ComprehensiveSearch;
